import React, { useEffect, useState } from "react";

import Modal from "../ui/modal";
import AppearanceSettings from "./AppearanceSettings";
import McpBridgeSettings, { mcpBridgeUrl } from "./McpBridgeSettings";
import NavEntriesSettings from "./NavEntriesSettings";
import OrchestratorSettings from "./OrchestratorSettings";

import "./settings-dialog.css";

/**
 * The product's own settings, in one place.
 * ---------------------------------------------------------------------------
 * They used to be wherever the thing they set happened to live - the sizes
 * behind the dashboard's edit bar, which meant editing a board to change how
 * the tabs are drawn. Settings are not a step in a task, so they are a door
 * off the rail instead, under the rest of the estate and above the way out.
 *
 * The dialog is a list of groups down the left and one of them on the right:
 * the shape every settings window has, so a group can be added without the
 * window being redesigned around it. The group that is open is remembered for
 * the visit - coming back to the one you were in is what someone setting two
 * things in a row expects.
 * ---------------------------------------------------------------------------
 */

/* Each group names itself, says what it is for in one line, and brings its own
   panel. `soon` marks the ones whose contents are still being decided: they
   are listed rather than hidden, because what a settings window HAS is part of
   knowing the product. */
export const SETTINGS_GROUPS = [
  {
    id: "appearance",
    label: "Appearance",
    hint: "How this screen is drawn — kept on this machine",
    mark: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3v18M3 12h18" opacity="0.5" />
      </svg>
    ),
  },
  {
    id: "orchestrator",
    label: "Orchestrator settings",
    hint: "What the estate is scanned with, and how the orchestrator reaches it",
    mark: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3.5" y="4" width="17" height="6" rx="1.8" />
        <rect x="3.5" y="14" width="17" height="6" rx="1.8" />
        <path d="M7.5 7h.01M7.5 17h.01" />
      </svg>
    ),
  },
  {
    id: "mcp",
    label: "MCP bridge",
    hint: "The bridge Forge reaches your repositories and tools through",
    /* Only where the tenant has one: `"mcp-bridge"` in tenant_info.json says
       whether this installation has a bridge and where it lives, and a group
       for something an installation does not have is a question with no answer
       behind it. */
    when: () => !!mcpBridgeUrl(),
    mark: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M7 8V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
        <rect x="3.5" y="8" width="17" height="6" rx="2" />
        <path d="M8 14v3a3 3 0 0 0 3 3h2a3 3 0 0 0 3-3v-3" />
      </svg>
    ),
  },
  {
    id: "menu",
    label: "Menu entries",
    hint: "What the rail down the side of the page carries, and in what order",
    mark: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3.5" y="4" width="6" height="16" rx="1.6" />
        <path d="M13 7h7M13 12h7M13 17h7" />
      </svg>
    ),
  },
];

const LAST_GROUP = "ui.settings.group";

/* The groups this installation actually has. Read at render, not at import:
   the tenant file is fetched while the app boots, so a list settled on the
   first evaluation would be the list from before it arrived. */
export function settingsGroups() {
  return SETTINGS_GROUPS.filter(g => (g.when ? g.when() : true));
}

export default function SettingsDialog({ open, onClose, isDarkMode, connected }) {
  const [at, setAt] = useState(() => {
    try { return sessionStorage.getItem(LAST_GROUP) || "appearance"; } catch (e) { return "appearance"; }
  });

  useEffect(() => {
    try { sessionStorage.setItem(LAST_GROUP, at); } catch (e) { /* private window */ }
  }, [at]);

  const groups = settingsGroups();
  const group = groups.find(g => g.id === at) || groups[0];

  return (
    <Modal open={open} onClose={onClose} close={false} className="settings-dialog">
      <div className={`settings${isDarkMode ? "" : " is-light"}`} aria-label="Settings">
        {/* ── the groups ── */}
        <nav className="settings__rail" aria-label="Settings groups">
          <span className="settings__title">Settings</span>

          {groups.map(g => (
            <button
              key={g.id}
              type="button"
              className={`settings__group${g.id === at ? " is-on" : ""}`}
              aria-current={g.id === at}
              onClick={() => setAt(g.id)}
            >
              <span className="settings__mark" aria-hidden="true">{g.mark}</span>
              <span className="settings__name">{g.label}</span>
            </button>
          ))}
        </nav>

        {/* ── and the one that is open ── */}
        <section className="settings__panel">
          <header className="settings__head">
            <div className="settings__ident">
              <h3 className="settings__heading">{group.label}</h3>
              <span className="settings__hint">{group.hint}</span>
            </div>
            <button type="button" className="settings__done" onClick={onClose}>Done</button>
          </header>

          <div className="settings__body">
            {group.id === "appearance" && <AppearanceSettings isDarkMode={isDarkMode} compact />}
            {group.id === "mcp" && <McpBridgeSettings isDarkMode={isDarkMode} />}
            {group.id === "menu" && <NavEntriesSettings isDarkMode={isDarkMode} />}

            {/* The orchestrator's own settings - all three of its tabs -
                drawn without their dialog. Mounted only while the group is
                open, since opening it is what asks the orchestrator for its
                configuration. */}
            {group.id === "orchestrator" && (
              <OrchestratorSettings
                isDarkMode={isDarkMode}
                connected={connected}
                onClose={onClose}
              />
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}
